import { Component, OnInit } from '@angular/core'
import { Store } from '@ngrx/store'
import { AppState } from '../../store/state/app.state'
import { DatePipe } from '@angular/common'
import { FeedbackResponseService } from '../../services/feedback/feedback-response.service'
import { ActivatedRoute } from '@angular/router'
import {
  getFeedbackDetailFromState, getNextUnInteractedFeedbackId
} from '../../store/selector/initialize.selector'
import { FeedbackQuestionAPIModel } from '../../models/api.model'
import { FormBuilder, FormControl, FormGroup } from '@angular/forms'
import { GlobalConstants } from '../../constants/global-constants'
import { CommonService } from '../../services/common/common.service'
import { tap } from 'rxjs'
import { show_error_action, show_success_msg_after_submit_action } from '../../store/action/common/common.action'

@Component({
  selector: 'app-feedback-response',
  templateUrl: './feedback-response.component.html',
  styleUrls: ['./feedback-response.component.scss']
})
export class FeedbackResponseComponent implements OnInit {
  feedbackForm: FormGroup
  forType: string
  forDisplayId: string
  forName: string
  feedbackId: string
  questions: FeedbackQuestionAPIModel[]
  selectedRating: number[] = []
  nextFeedbackId: string
  uiForType: string

  constructor(
    private appState: Store<AppState>,
    private datePipe: DatePipe,
    private feedbackService: FeedbackResponseService,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private cs: CommonService
  ) {
    this.feedbackForm = this.fb.group({
      description: ['']
    })
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.feedbackId = params['id']
      if (this.feedbackId) {
        this.feedbackService.dispatchUpdateFeedbackStateUiStatus(this.feedbackId)
        this.appState.select(getFeedbackDetailFromState(this.feedbackId)).subscribe((feedbackDetail) => {
          if (feedbackDetail) {
            this.forType = feedbackDetail.forType
            this.forName = feedbackDetail.forName
            this.forDisplayId = feedbackDetail.forDisplayId
            this.getTemplateForResponse()
            if (this.forType === GlobalConstants.MODULE.QUERIES) {
              this.uiForType = 'Query'
            }
          }
        })
      }
    })
  }

  getTemplateForResponse() {
    this.feedbackService.fetchQuestionsFromTemplate(this.forType).subscribe((questions) => {
      if (!this.questions && questions) {
        this.questions = questions.sort((a, b) => a.sequence - b.sequence)
        this.addFormControls()
        this.getNextUnInteractedFeedbackId()
      }
    })
  }

  addFormControls() {
    const formControls = {}

    this.questions.forEach((question) => {
      formControls[`response${question.sequence}`] = ['']
    })

    Object.keys(formControls).forEach(controlName => {
      this.feedbackForm.addControl(controlName, new FormControl(''))
    })
  }

  getFormControlName(sequence: number): string {
    return `response${sequence}`
  }

  setRating(questionIndex: number, rating: number) {
    this.selectedRating[questionIndex] = rating

    const formControlName = this.getFormControlName(questionIndex)

    if (this.feedbackForm.get(formControlName)) {
      this.feedbackForm.get(formControlName)?.setValue(rating)
    }
  }

  submitFeedbackResponse() {
    const feedbackStatus = { id: this.feedbackId }
    const templateId = this.questions.length > 0 ? this.questions[0].template.id : ''
    const feedbackTemplate = { id: templateId }

    if (this.canSubmitFeedback()) {
      const feedbackResponse = {
        ...this.feedbackForm.getRawValue(),
        feedback: feedbackStatus,
        template: feedbackTemplate
      }

      this.feedbackService.saveFeedbackResponse(feedbackResponse)
        .pipe(
          tap({
            next: (response) => {
              this.appState.dispatch(show_success_msg_after_submit_action({ moduleName: 'Feedback Response' }))
              this.decideNavigation()
            },
            error: (error) => {
              this.appState.dispatch(show_error_action(error))
            }
          })
        )
        .subscribe()
    }
  }

  dismissFeedbackResponse() {
    this.feedbackService.updateFeedbackStatus(this.feedbackId, GlobalConstants.FEEDBACK_STATUS.DISMISSED)
      .pipe(
        tap({
          next: (response) => {
            this.decideNavigation()
          },
          error: (error) => {
            this.appState.dispatch(show_error_action(error))
          }
        })
      )
      .subscribe()
  }

  remindMeLater() {
    this.decideNavigation()
  }

  decideNavigation() {
    this.getNextUnInteractedFeedbackId()
    this.resetForm()
    if (this.nextFeedbackId) {
      this.cs.routeTo(`/${GlobalConstants.ROUTING.FEEDBACK}/${this.nextFeedbackId}`)
    } else {
      this.cs.routeTo(GlobalConstants.ROUTING.DASHBOARD)
    }
  }

  getNextUnInteractedFeedbackId() {
    this.appState.select(getNextUnInteractedFeedbackId(this.feedbackId)).subscribe((nextFeedbackId) => {
      if (nextFeedbackId) {
        this.nextFeedbackId = nextFeedbackId
      } else {
        this.nextFeedbackId = null
      }
    })
  }

  get f() {
    return this.feedbackForm?.controls
  }

  resetForm() {
    this.feedbackForm.reset()
    this.selectedRating = []
  }

  private canSubmitFeedback() {
    const lastSequence = this.questions[this.questions.length - 1].sequence
    const lastSequenceRating = this.selectedRating[lastSequence]

    const isRated = this.selectedRating.some(rating => rating !== undefined)

    if (isRated) {
      const descriptionValue = this.feedbackForm.get('description').value
      if (lastSequenceRating && lastSequenceRating < 3 && !descriptionValue) {
        this.cs.showError('Please provide additional remarks in the description')
      } else return true
    } else {
      this.cs.showError('Please provide ratings')
    }
    return false
  }
}
